import { ref, uploadBytes } from 'firebase/storage';
import { fsCall_generic } from 'global/cnr/contexts/contextHelpers/fcCalls';
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess';
import { useCache } from 'global/cnr/contexts/useHelpers/useCache';
import { useOptionsLeft } from 'global/cnr/contexts/useHelpers/useOptionsLeft';
import _ from 'lodash';
import { Suspense, useContext, useEffect, useReducer, useState } from 'react';
import { Icon, Image, Input, Label, Menu, Sidebar } from 'semantic-ui-react';
import { _useAppSlimMode } from '../../App';
import { _appUrlPaths, _projectAppNames, envTypes } from '../../project/appConfiguration';
import { DemoContext } from '../cnr/contexts/DemoContext';
import { FrameworkContext } from '../cnr/contexts/FrameworkContent';
import { GoogleSheetsContext } from '../cnr/contexts/GoogleSheetsContext';
import { PageContext } from '../cnr/contexts/PageContext';
import { ParentContext } from '../cnr/contexts/ParentContext';
import { frameworkLeftTypes } from '../cnr/reducers/FrameworkReducer';
import { leftMenuHandlers, leftMenuReducer, leftMenuTriggerTypes, leftMenuTypes } from '../cnr/reducers/LeftMenuReducer';
import { defaultHelpers, withDefaultProps } from '../common/defaultProps';
import { uniqueKey } from "../common/keys";
import SuspenseDimmer from '../components/alerts/SuspenseDimmer';
import { appIconTypes } from '../enums/appIconTypes';
import { gEnums } from '../enums/globalEnums';
import { openExternal } from '../viewSettings/helpers/settingsLinks';
import TransitionWrapper, { transitionWrapperTypes } from '../wrappers/TransitionWrapper';
import Wrapper, { wrapperTypes } from '../wrappers/Wrapper';
import ItemMenuSquares, { _imsTypes } from 'global/wrappers/ItemMenuSquares';
import { fbAppConfigs } from 'project/appConfigs';
import { useGoogleSheets } from 'global/cnr/contexts/useHelpers/useGoogleSheets';
import { useAppConfig } from 'global/cnr/contexts/useHelpers/useAppConfig';
import { allDataModes } from 'global/components/viewers/AppDashboard';

const showCorner = false

const SettingsAppLeftMenu = () => {

  // parentContext
  const parentContext = useContext(ParentContext);
  const { states, handlers, fns, fn_cls } = parentContext ?? {}
  const { currentSettings_state, app_state, appSettings_state, paps_state, manifest_state, eventInfo_state } = states
  const { appUser_handlers, appSettings_handlers, eventInfo_handlers, serviceWorker_handlers } = handlers
  const { appUser_fns, navigation_fns } = fns
  const appUserAccess = useAppUserAccess()
  const { isSuperAdminDeveloper, appUserAccessType } = appUserAccess ?? {}
  const authSettingsPermissionType = appUser_fns.validate_trueSettingsAuth(appUserAccess)
  const { mainFunctions, mainStorage } = app_state ?? {}
  const { appDataDocuments } = eventInfo_state ?? {}

  // pageContext
  const pageContext = useContext(PageContext)
  const { homeSettings_global } = pageContext ?? {}
  const { settingsOptions } = homeSettings_global ?? {}
  const { useGutterText, gutterText, poweredByText } = settingsOptions ?? {}

  const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appConfigSettings } = appSettings_state ?? {}
  const { isHomePage, appArea, pathViews, pathModes, view } = paps_state ?? {}
  const { googleSheets, projectOptions } = aps_global ?? {}
  const { googleSheetsId } = googleSheets ?? {}
  const { appNames } = appConfigSettings ?? {}
  const { appEnv } = appNames ?? {}
  const { useEventGutterText, eventGutterText, eventPoweredByText, ignorePoweredBy } = projectOptions ?? {}
  const { appComponents, themeColors } = aps_global ?? {}
  const { componentContent } = appComponents ?? {}
  const { color1 } = themeColors ?? {}

  const { getDataFromGoogleSheets } = useGoogleSheets()
  const { changeDatabase } = useAppConfig()

  const _useGutterText = _useAppSlimMode ? false : useEventGutterText || useGutterText
  const _gutterText = eventGutterText || gutterText
  const _poweredByText = eventPoweredByText ? eventPoweredByText : poweredByText

  const hexToRGBA = (hex, opacity) => {
    let r = parseInt(hex.slice(1, 3), 16);
    let g = parseInt(hex.slice(3, 5), 16);
    let b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const gutterStyle = useEventGutterText ? {
    background: `linear-gradient(to bottom, ${color1}, ${hexToRGBA(color1, 0)})`
  } : {};

  let _gutterClass = 'app-me'

  if (!ignorePoweredBy) {
    _gutterClass += ' pb'
  }

  const { isSuperAdmin: _isSuperAdmin, isSuperAdminDeveloper: _isSuperAdminDeveloper } = appUserAccess ?? {}

  // frameworkContext
  const frameworkContext = useContext(FrameworkContext);
  const { framework_state, framework_handlers } = frameworkContext ?? {}
  const { showLeftMenuCaptions } = framework_state ?? {}

  // demoContext
  const demoContext = useContext(DemoContext)
  const { demo_handlers } = demoContext ?? {}

  // googleSheetsContext
  const googleSheetsContext = useContext(GoogleSheetsContext)
  const { googleSheets_handlers } = googleSheetsContext ?? {}

  let dashboardType;

  if (pathModes.sport) {
    dashboardType = 'Sports'
  } else if (pathModes.district) {
    dashboardType = 'District'
  } else if (pathModes.organization) {
    dashboardType = 'Organization'
  }

  let allowGf = false

  switch (componentContent) {
    case gEnums.componentContentTypes.golfEvent:
      allowGf = true
      dashboardType = 'Golf'
      break;
    default:
    // nothing
  }

  const initState = {}
  const [leftMenu_state, leftMenu_dispatch] = useReducer(leftMenuReducer, initState);
  const leftMenu_handlers = leftMenuHandlers(leftMenu_dispatch)
  const { currentMenuType, triggerActionType, frt, altTrigger } = leftMenu_state ?? {}
  const { handleSet_currentMenuType } = leftMenu_handlers ?? {}

  const leftOptions = useOptionsLeft({ leftMenu_handlers })

  const [gsKey, setGsKey] = useState()
  const [showMenuItems, setShowMenuItems] = useState(false)
  const [menuVisible, setMenuVisible] = useState(false);

  const cacheProps = useCache()

  useEffect(() => {
    switch (currentMenuType) {
      case leftMenuTypes.actions:
      case leftMenuTypes.admins:
      case leftMenuTypes.appAdmins:
      case leftMenuTypes.appHistory:
      case leftMenuTypes.appInfos:
      case leftMenuTypes.appUsers:
      case leftMenuTypes.confirmation:
      case leftMenuTypes.dashboards:
      case leftMenuTypes.dataOrigins:
      case leftMenuTypes.dataSources:
      case leftMenuTypes.devices:
      case leftMenuTypes.externals:
      case leftMenuTypes.input:
        setShowMenuItems(true)
        break;

      default:
        // this is the default
        setShowMenuItems(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps 
  }, [currentMenuType]);

  useEffect(() => {

    if (triggerActionType) {

      switch (triggerActionType) {

        case leftMenuTriggerTypes.confirmAction:

          switch (altTrigger) {

            // case leftMenuTriggerTypes.appDataDocuments:
            //   eventInfo_handlers.handleCreate_appDataDocuments(null, true)
            //   break;

            case leftMenuTriggerTypes.appDataLinks:
              eventInfo_handlers.handleCreate_appDataLinks(true)
              break;

            // case leftMenuTriggerTypes.appProfiles:
            //   eventInfo_handlers.handleCreate_appProfiles()
            //   break;
          }
          break;

        case leftMenuTriggerTypes.toggle:
          framework_handlers.handleToggle_leftMenu()
          break;

        case leftMenuTriggerTypes.triggerAction:
          switch (frt) {
            case frameworkLeftTypes.refreshCache:
              serviceWorker_handlers.handleGet_cache()
              break;
            case frameworkLeftTypes.clearCache:
              cacheProps.clearAllCache()
              serviceWorker_handlers.handleClear_cache()
              break;
            case frameworkLeftTypes.clearCacheData:
              serviceWorker_handlers.handleClear_cache(null, true)
              break;
            case frameworkLeftTypes.clearCacheSettings:
              serviceWorker_handlers.handleClear_cache(null, false, true)
              break;
            case frameworkLeftTypes.updateManifest:
              updateStorageManifest(fn_cls, mainStorage, manifest_state?.manifestInfo)
              break;
            case frameworkLeftTypes.refreshGoogleSheets:
              getDataFromGoogleSheets(
                {
                  gsId: googleSheetsId,
                  callback: googleSheets_handlers.handleResponse_googleSheet
                }
              )
              break;
          }
          break;

        case leftMenuTriggerTypes.frameworkLeft:
          framework_handlers.handleShow_frameworkLeft(view, frt)
          break;

        case leftMenuTriggerTypes.appHistory:
          navigation_fns.handleNav_path(frt)
          break;

        case leftMenuTriggerTypes.appFunction:
          fsCall_generic(mainFunctions, 'testFunction')
          break;

        case leftMenuTriggerTypes.appUserAccess:
          switch (frt.value) {
            case 'r':
              appUser_handlers.handleResetAccessLevel()
              break;
            default:
              appUser_handlers.handleSet_tempAccessLevel(frt.value, pathViews)
          }
          break;

        case leftMenuTriggerTypes.dashboard:
          framework_handlers.handleShow_appDashboard(frt)
          break;

        case leftMenuTriggerTypes.dataOrigin:
          switch (frt) {
            case _projectAppNames.pojo:
              changeDatabase(_projectAppNames.pojo)
              break
            default:
            // nothing
          }
          break;

        case leftMenuTriggerTypes.dataSource:
          appSettings_handlers.handleDataSourceType(frt)
          break;

        case leftMenuTriggerTypes.device:
          appSettings_handlers.handleSetDevice(frt)
          break;

        case leftMenuTriggerTypes.settings:
          framework_handlers.handleShow_frameworkLeft(view, frt)
          // appSettings_handlers.handleToggle_settingsLeftOn()
          break;

        case leftMenuTriggerTypes.search:
          appSettings_handlers.handleToggle_settingsLeftOn()
          break;

        case leftMenuTriggerTypes.openDb:
          openExternal.db(pathViews)
          break;

        case leftMenuTriggerTypes.settingsDb:
          openExternal.settingsDb(pathViews, appArea)
          break;

        case leftMenuTriggerTypes.openStorage:
          openExternal.db(pathViews, true)
          break;

        case leftMenuTriggerTypes.openGs:
          openExternal.googleSheets(googleSheetsId)
          break;

        case leftMenuTriggerTypes.gotoClient:
          navigation_fns.handleNav_path('/clients/' + pathViews.clients)
          break;

        case leftMenuTriggerTypes.gotoHome:
          demo_handlers.handleResetDemo()
          navigation_fns.handleNav_path('/')
          break;

        case leftMenuTriggerTypes.gotoDemo:
          demo_handlers.handleResetDemo()
          navigation_fns.handleNav_path('/demo')
          break;

        case leftMenuTriggerTypes.gotoDev:
          handleOpen_otherSite(true)
          break;

        case leftMenuTriggerTypes.gotoSite:
          handleOpen_otherSite()
          break;

        case leftMenuTriggerTypes.createNewEvent:
          console.log('create new event')
          break;

        case leftMenuTriggerTypes.dataOverride:
          appSettings_handlers.handleToggle_dataOverrideOn()
          break;

        case leftMenuTriggerTypes.demoOverride:
          appSettings_handlers.handleToggle_demoOverrideOn()
          break;

        case leftMenuTriggerTypes.prodOverride:
          appSettings_handlers.handleToggle_prodOverrideOn()
          break;

        case leftMenuTriggerTypes.help:
          appSettings_handlers.handleToggle_helpOn()
          break;

        case leftMenuTriggerTypes.auth:
          appSettings_handlers.handleToggle_authOn()
          break;

        case leftMenuTriggerTypes.settingsPreview:
          appSettings_handlers.handleToggle_previewOn(true)
          break;

        case leftMenuTriggerTypes.altDatabase:
          appSettings_handlers.handleToggle_altDatabaseOn(true)
          break;
        default:
        // nothing
      }
      handleSet_currentMenuType()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    triggerActionType,
  ]);


  const handleGsKey = (e, d) => setGsKey(d.value)

  const getUrl = () => {
    appSettings_handlers.handleDataSourceType(gEnums.dataSourceTypes.googleSheet, gsKey)
  }

  const handleOpen_otherSite = (toDev) => {
    const { clients, events } = pathViews ?? {}
    let suffix = '';
    if (events) {
      suffix = 'clients/' + clients + '/events/' + events + '/landing'
    } else if (clients) {
      suffix = 'clients/' + clients
    }
    if (toDev) {
      window.open(_appUrlPaths.dev + suffix, '_blank')
    } else {
      switch (appEnv) {
        case envTypes.development:
          window.open(_appUrlPaths.prod + suffix, '_blank')
          break;
        case envTypes.production:
          window.open(_appUrlPaths.dev + suffix, '_blank')
          break;
      }
    }

  }

  const handleShow_confirm = (props) => handleSet_currentMenuType({ lmt: leftMenuTypes.confirmation }, props)
  const handleShow_input = (props) => handleSet_currentMenuType({ lmt: leftMenuTypes.input }, props)

  const icon = (item) => {
    const _item = withDefaultProps(item, defaultHelpers.iconDefaults);
    const { icon, color, iconCorner, iconCornerColor, showIconCorner } = _item
    if (iconCorner && (showCorner || showIconCorner)) {
      return <Icon.Group as={'i'}>
        <Icon name={icon} color={color ? color : 'red'} />
        <Icon corner={true} name={iconCorner} color={iconCornerColor ? iconCornerColor : 'blue'} />
      </Icon.Group>
    } else {
      return <Icon name={icon} color={color ? color : null} />
    }
  }

  const input = () => <Input
    value={gsKey}
    onChange={handleGsKey}
    action={{ icon: appIconTypes.arrowRight, onClick: () => { getUrl() } }} />

  const menuItems = (opts) => {

    const mis = _.map(opts, (item, key) => {
      const { caption, count, counts, description, dividerBefore, text, oc, showCaption, showInput, updating, useConfirm, useInput, opts, src } = item ?? {}
      const { ocn } = opts ?? {}
      const _caption = showLeftMenuCaptions || showCaption ? text : caption
      let cn = dividerBefore ? 'sbm-top' : ''
      if (item.cn) { cn += ' ' + item.cn }
      if (_caption || src) { cn += ' left-sub' }
      if (updating) { cn += ' blink_me_p' }
      if (ocn) { cn += ' ' + ocn }
      let _oc = useConfirm ? handleShow_confirm : oc
      if (useInput) { _oc = handleShow_input }

      return <Menu.Item
        key={uniqueKey('svs', 'mmi', key)}
        name={item.text}
        onClick={() => _oc && _oc({ frt: item.frt, lmt: item.lmt, lma: item.lma })} title={text}
        className={cn}
      >
        {!showInput && icon(item)}
        {count && <Label attached='bottom right' size='mini' color='black'>{count}</Label>}
        {counts?.tl && <Label attached='top left' size='mini' color='black'>{counts.tl}</Label>}
        {counts?.tr && <Label attached='top right' size='mini' color='black'>{counts.tr}</Label>}
        {counts?.br && <Label attached='bottom right' size='mini' color='black'>{counts.br}</Label>}
        {showInput && input()}
        {!showInput && _caption && _caption}
        {description && description}
        {src && <div><Image size='mini' src={src} /></div>}
      </Menu.Item>
    })
    return mis
  }

  const header = () => <div>
    <Icon
      name='arrow left'
      color={'blue'}
      style={{ marginRight: '1em' }}
      onClick={() => handleSet_currentMenuType()} />
    {_.startCase(currentMenuType) + ' Options'}
  </div>

  const fnAlt = (item) => {
    switch (item?.frt) {
      case allDataModes.appUsers:
        const count = _.size(appDataDocuments?.attendees)
        return <Label color={'blue'} size='mini' content={count} />
      case allDataModes.googleDashboard:
        const { allowGoogleSheets } = googleSheets ?? {}
        return <Icon name={'check circle'} color={allowGoogleSheets ? 'green' : 'grey'} />
      default:
        return <Icon name={'arrow right'} color={'grey'} />
    }
  }

  const menu_selected = () => {
    const hc = (item) => {
      item?.oc && item.oc({ frt: item.frt, lmt: item.lmt, lma: item.lma })
    }
    const ims = <ItemMenuSquares
      menuItems={leftOptions[currentMenuType]}
      onClick={hc}
      imsType={_imsTypes.inline}
      fn_altContent={fnAlt}
    />
    return <Wrapper
      header={header()}
      content={ims}
      wrapperType={wrapperTypes.paddedHeaderAndFooter}
    />
  }

  const menu_main = () => {
    return <TransitionWrapper transitionWrapperType={transitionWrapperTypes.basic}>
      <Menu
        inverted
        icon
        vertical
        className='frm-right menu-salm'
        style={{
          opacity: menuVisible ? 1 : 0,
          pointerEvents: menuVisible ? 'auto' : 'none',
          transition: 'opacity 0.5s ease',
        }}
      >
        <div>{menuItems(leftOptions.dashboard)}</div>
        <div>{menuItems(leftOptions.action)}</div>
        <div>{menuItems(leftOptions.appUser)}</div>
        <div>{menuItems(leftOptions.device)}</div>
        {isHomePage && isSuperAdminDeveloper && <div>{menuItems(leftOptions.dataOrigin)}</div>}
        <div>{menuItems(leftOptions.dataSource)}</div>
        <div>{menuItems(leftOptions.overrides)}</div>
        <div>{menuItems(leftOptions.external)}</div>
        <div>{menuItems(leftOptions.home)}</div>
      </Menu>
      {_useGutterText && _gutterText &&
        <div
          className={_gutterClass}
          style={gutterStyle}
        >
          <div>{_gutterText}</div>
          {!ignorePoweredBy && <div>{_poweredByText ? _poweredByText : 'Powered by Meeting Evolution'}</div>}
        </div>
      }
    </TransitionWrapper>
  }

  let cn = 'sbr-psh-l'

  if (showMenuItems) {
    cn += ' vw'
  }

  const sidebar = () => <Sidebar.Pushable className={cn} >
    {showMenuItems && menu_selected()}
    <Sidebar.Pusher
      onMouseEnter={() => setMenuVisible(true)}
      onMouseLeave={() => setMenuVisible(false)}
      dimmed={showMenuItems}
      className={'h100'}
    >
      {menu_main()}
    </Sidebar.Pusher>
  </Sidebar.Pushable>

  return <Suspense fallback={<SuspenseDimmer caption={'appSub'} origin={'SettingsAppLeftMenu'} />}>
    {leftOptions && sidebar()}
  </Suspense>

}

export default SettingsAppLeftMenu

const updateStorageManifest = async (fn_cls, mainStorage, manifestInfo) => {

  const appKeys = {
    dev: fbAppConfigs.meetingevolution.dev.projectId,
    prod: fbAppConfigs.meetingevolution.prod.projectId,
  }

  const { settingsDocName, manifest } = manifestInfo ?? {}
  const _manifest = JSON.stringify(manifest, null, 2)
  const manifestRef = ref(mainStorage, 'allManifests/' + settingsDocName);
  const blob = new Blob([_manifest], { type: 'application/json' });

  const cb = (res) => {
    console.log('res', res)
  }

  const bucketFolder = 'allManifests'
  const projectIds = {
    source: appKeys.dev
  }

  try {
    await uploadBytes(manifestRef, blob);
    fn_cls.makeAllManifestsPublic(projectIds, bucketFolder, cb)
    console.log(`✅ Manifest uploaded as ${settingsDocName}.json`);
  } catch (err) {
    console.error("❌ Error uploading manifest:", err);
  }
}